import React, {useCallback, useMemo, useReducer, type ComponentProps} from 'react';
import {getGetResourcesQueryKey, useGetResources, type ResourceType} from 'api/generated';
import {useNode} from '@craftjs/core';
import {queryClient} from 'App';
import {imgNone} from 'assets';
import {useEditorStyle} from 'hooks';
import {formatColorResult} from 'utils';
import {ColorPicker, Divider, InputText} from 'components';

import ModalResourceAdd from './components/Modal.Resource.Add';
import EditorInspectorVisible from '../../inspectors/Editor.Inspector.Visible';
import EditorInspectorDimension from '../../inspectors/Editor.Inspector.Dimension';
import EditorInspectorMargin from '../../inspectors/Editor.Inspector.Margin';

interface IEditorInspectorImageProps {
  projectUid: string;
}

const EditorInspectorImage = ({projectUid}: IEditorInspectorImageProps) => {
  const [showModal, toggleModal] = useReducer(s => !s, false);

  const {data: resourcesData} = useGetResources({filter: 'mine'}, {query: {staleTime: 10 * 1000, enabled: showModal}});
  const resources = resourcesData?.data?.data ?? [];

  const handleUploaded = useCallback((_resource: ResourceType) => {
    queryClient.invalidateQueries({queryKey: [getGetResourcesQueryKey({filter: 'mine'})[0]]});
  }, []);

  const {
    src,
    alt,
    actions: {setProp},
  } = useNode<ComponentProps<'img'>>(node => node.data.props);

  const addedResources = useMemo(() => {
    const resource = resources.find(r => r.fileUrl === src);
    return resource ? [resource] : [];
  }, [src, resources]);

  const handleConfirm = useCallback(
    (resources: ResourceType[]) => {
      const src = resources[0].fileUrl ?? '';
      setProp((prop: HTMLImageElement) => (prop.src = src));
      toggleModal();
    },
    [setProp],
  );

  const {device, style, updateStyle} = useEditorStyle({setProp});

  return (
    <div key={device}>
      <h4 className="text-slate-800 dark:text-white font-semibold">Image</h4>
      <Divider />
      <EditorInspectorVisible style={style} setProp={setProp} />
      <Divider />
      <button type="button" className="relative w-full aspect-square rounded-md overflow-hidden cursor-pointer" onClick={toggleModal}>
        <div className="absolute inset-0 hover:bg-black opacity-50"></div>
        <img src={src || imgNone} alt="inspector-img" className="w-full h-full object-contain" />
      </button>
      <Divider />
      <InputText label="URL" defaultValue={src} onChange={e => setProp((prop: HTMLImageElement) => (prop.src = e.target.value))} />
      <InputText label="Alt" defaultValue={alt} onChange={e => setProp((prop: HTMLImageElement) => (prop.alt = e.target.value))} />
      <Divider />
      <div className="flex items-center justify-between">
        <p className="text-sm">Background</p>
        <ColorPicker
          alignment="right"
          color={style?.backgroundColor ?? 'white'}
          onPicked={color => {
            updateStyle('backgroundColor', formatColorResult(color));
          }}
        />
      </div>
      <Divider />
      <EditorInspectorDimension style={style} setProp={setProp} />
      <Divider />
      <EditorInspectorMargin style={style} setProp={setProp} />
      <ModalResourceAdd
        visible={showModal}
        onClose={toggleModal}
        onConfirm={handleConfirm}
        onUploaded={handleUploaded}
        addedResources={addedResources}
        resources={resources}
        projectUid={projectUid}
      />
    </div>
  );
};

export default React.memo(EditorInspectorImage);
